import { useCallback } from 'react';
import useSWR from 'swr';
import { useAuth } from './useAuth';
import { apiClient } from '@/api';

export interface Integration {
  id: string;
  provider: string;
  name: string;
  description?: string;
  cloud_id?: string;
  site_url?: string;
  site_name?: string;
  is_active: boolean;
  last_used_at?: string;
  created_at: string;
  updated_at: string;
}

interface IntegrationsResponse {
  integrations: Integration[];
}

export function useIntegrations() {
  const { session } = useAuth();

  const endpoint = '/api/v1/integrations';

  const fetcher = async (url: string): Promise<IntegrationsResponse> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    return apiClient.get<IntegrationsResponse>(url, { token: session.access_token });
  };

  const { data, error, isLoading, mutate } = useSWR<IntegrationsResponse>(
    session?.access_token ? endpoint : null,
    fetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: 5000,
    }
  );

  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  const deleteIntegration = useCallback(async (id: string): Promise<void> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    await apiClient.delete(`/api/v1/integrations/${id}`, { token: session.access_token });
    await mutate();
  }, [session, mutate]);

  return {
    integrations: data?.integrations ?? [],
    isLoading,
    error: error ?? null,
    refresh,
    deleteIntegration,
  };
}
